import { ApiError } from '@/lib/api';
import type { SpaceDetail, SpaceRole } from '@/types/spaces';
import { FULL_SHARE, parsePercentage, percentageTotal } from './creation';

export type PercentageDraft = Record<string, string>;

export interface SpaceSettingsAccess {
  // Editors of an open space change people, percentages and permissions.
  manage: boolean;
  // Percentages only divide expenses in the split model.
  percentages: boolean;
  closeTrip: boolean;
}

export function settingsAccess(space: SpaceDetail): SpaceSettingsAccess {
  const manage = space.myRole === 'editor' && !space.archivedAt;
  return { manage, percentages: manage && space.model === 'split', closeTrip: manage && space.kind === 'trip' };
}

// The fields start from each person's REAL share (`effectivePct`): a space whose stored values do not add up to 100
// (two people at 100 each, a newcomer's share on top) shows what everyone actually pays, never a number the API
// rescales behind the scenes. Saving sends exactly what was typed, which must add up to 100%.
export function percentageDraft(space: SpaceDetail): PercentageDraft {
  return Object.fromEntries(space.members.map((member) => [member.userId, formatPct(member.effectivePct)]));
}

export function formatPct(value: number): string {
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 2, useGrouping: false });
}

export interface PercentageReview {
  errors: Record<string, string>;
  // Sum of the draft in hundredths of a percent; null while any value is invalid.
  total: number | null;
  dirty: boolean;
  // What a save sends: only when every value is valid and they add up to exactly 100%.
  members: { userId: string; pct: number }[] | null;
}

export function reviewPercentages(space: SpaceDetail, draft: PercentageDraft): PercentageReview {
  const parsed = space.members.map((member) => ({ userId: member.userId, pct: parsePercentage(draft[member.userId] ?? '') }));
  const errors: Record<string, string> = {};
  for (const entry of parsed) if (entry.pct === null) errors[entry.userId] = 'Use um percentual de 0 a 100, com até 2 casas decimais.';
  // Compared at the editable precision: an equalized 33.333… reads back as 33,33.
  const dirty = space.members.some((member) => parsePercentage(draft[member.userId] ?? '') !== parsePercentage(formatPct(member.effectivePct)));
  const total = percentageTotal(space.members.map((member) => draft[member.userId] ?? ''));
  const members = total === FULL_SHARE ? parsed.map((entry) => ({ userId: entry.userId, pct: entry.pct as number })) : null;
  return { errors, total, dirty, members };
}

export const ROLE_LABELS: Record<SpaceRole, string> = { editor: 'Editor', viewer: 'Leitura' };

// The API answers rule violations (last editor, closed space, …) with a pt-BR sentence meant for the person.
export function settingsError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  if (error.status === 404) return 'Este espaço ou esta pessoa não está mais disponível. Atualize a página.';
  if (error.status === 403) return 'Só editores podem fazer esta alteração.';
  if ([400, 409].includes(error.status) && error.message && !error.message.startsWith('Request failed')) return error.message;
  return fallback;
}
